<template>
  <div class="terms">
    <section>
      <ul class="terms-list">
        <li><a href="#privacy_notice">Privacy notice</a></li>
        <li>
          <a href="#protecting_security"
            >Protecting the security of Government of Canada websites</a
          >
        </li>
        <li>
          <a href="#outside_files"
            >Using files located on non-Government of Canada Servers</a
          >
        </li>
        <li>
          <a href="#official_languages"
            >Providing content in our official languages</a
          >
        </li>
        <li>
          <a href="#website_linking"
            >Linking to non-Government of Canada websites</a
          >
        </li>
        <li>
          <a href="#ownership_content"
            >Ownership and usage of content provided on this site</a
          >
        </li>
        <li><a href="#trademark_notice">Trademark notice</a></li>
        <li>
          <a href="#accessibility_commitment"
            >Our commitment to accessibility</a
          >
        </li>
        <li><a href="#media_requests">Questions and media requests</a></li>
      </ul>
      <br />
    </section>
    <section id="privacy_notice">
      <h2 class="subtitle is-size-3">Privacy notice</h2>
      <p>
        The Government of Canada and BizPaL are committed to providing websites
        that respect the privacy of visitors. This privacy notice summarizes the
        privacy practices for BizPaL's online activities.
      </p>
      <br />
      <p>
        All personal information collected by this institution is governed by
        the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/P-21/index.html"
          >Privacy Act</a
        >. This means that you will be informed of the purpose for which your
        personal information is being collected and how to exercise your right
        of access to that information.
      </p>
      <br />
      <h3 class="subtitle is-size-4">Your privacy and the Internet</h3>
      <p>
        The nature of the Internet is such that Web servers automatically
        collect certain information about a visit to a website, including the
        visitor’s Internet Protocol (IP) address. IP addresses are unique
        numbers assigned by Internet Service Providers (ISP) to all devices used
        to access the Internet. Web servers automatically log the IP addresses
        of visitors to their sites. The IP address, on its own, does not
        identify an individual. However, in certain circumstances, such as with
        the cooperation of an ISP for example, it could be used to identify an
        individual using the site. For this reason, the Government of Canada
        considers the IP address to be personal information, particularly when
        combined with other data automatically collected when visitor requests a
        Web page such as the page or pages visited, date and time of the visit.
      </p>
      <br />
      <p>
        In cases where services are provided by organizations outside of the
        Government of Canada, such as social media platforms or mobile
        applications, IP addresses may be recorded by the Web server of the
        third-party service provider. Unless otherwise noted, BizPaL does not
        automatically gather any specific information from you, such as your
        name, telephone number or email address. BizPaL would obtain this type
        of information only if you supply it to us, for example, by email or by
        filling in a contact form. In such cases, how your personal information
        is handled will be provided in a Personal Information Collection
        Statement.
      </p>
      <br />
      <h3 class="subtitle is-size-4">
        Communicating with the Government of Canada
      </h3>
      <p>
        If you choose to send BizPaL an email or complete a feedback form
        online, your personal information is used by BizPaL in order to respond
        to your inquiry. The information you provide will only be shared with
        another government institution if your inquiry relates to that
        institution. BizPaL does not use the information to create individual
        profiles nor does it disclose the information to anyone other than to
        those in the federal government who need to provide you with a response.
        Any disclosure of your personal information is in accordance with the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/P-21/index.html"
          >Privacy Act</a
        >.
      </p>
      <br />
      <p>
        Emails and other electronic methods used to communicate with the
        Government of Canada are not secure unless it is specifically stated on
        a Web page. Therefore, it is recommended that you
        <strong>do not</strong> send sensitive personal information, such as
        your Social Insurance Number or your date of birth, through non-secure
        electronic means.
      </p>
      <br />
      <p>
        Personal information from emails or completed feedback forms is
        collected pursuant to the provision in Innovation, Science and Economic
        Development Canada’s enabling statutes. Such information may be used for
        statistical, evaluation and reporting purposes and is included in
        Personal Information Bank PSU 914-Public Communications.
      </p>
      <br />
      <h3 class="subtitle is-size-4">
        Improving your experience on Government of Canada websites
      </h3>
      <h4 class="subtitle">Digital markers (including cookies)</h4>
      <p>
        A digital marker is a resource created by the visitor's browser in order
        to remember certain pieces of information for the web server to
        reference during the same or subsequent visit to the website. Examples
        of digital markers are “cookies” or HTML5 web storage. Some examples of
        what digital markers do are as follows:
      </p>
      <ul class="terms-list">
        <li>
          they allow a website to recognize a previous visit each time the
          visitor accesses the site;
        </li>
        <li>
          they track what information is viewed on a site which helps website
          administrators ensure visitors find what they are looking for.
        </li>
      </ul>
      <p>
        BizPaL uses sessional and/or persistent digital markers on some portions
        of its website. During your online visit, your browser exchanges data
        with BizPaL's web server. The digital markers used do not allow BizPaL
        to identify individuals.
      </p>
      <br />
      <p>
        You may adjust your browser settings to reject digital markers,
        including cookies, if you so choose. However, it may affect your ability
        to interact with BizPaL's website.
      </p>
      <br />
    </section>
    <section>
      <h3 class="subtitle is-size-4">Web Analytics</h3>
      <p>
        Web analytics is the collection, analysis, measurement, and reporting of
        data about web traffic and visits for purposes of understanding and
        optimizing Web usage. Information in digital markers may be used for the
        purpose of web analytics to remember your online interactions with
        BizPaL's website.
      </p>
      <br />
      <p>
        BizPaL uses Google Analytics to improve its web site. When your computer
        requests a BizPaL web page, our institution collects the following types
        of information for web analytics:
      </p>
      <ul class="terms-list">
        <li>the originating IP address</li>
        <li>the date and time of the request</li>
        <li>the type of browser used</li>
        <li>the page(s) visited</li>
      </ul>
      <p>
        BizPaL uses Google Analytics and the information collected is disclosed
        to Google Inc., an external third party service provider. Your IP
        address is anonymized prior to being stored on the service provider's
        servers in order to help safeguard your privacy. The information
        collected is de-personalized by activating the anonymization feature in
        Google Analytics.
      </p>
      <br />
      <p>
        Data collected for web analytics purposes goes outside of Canada to the
        United States and may be subject to the governing legislation of that
        country, the USA
        <a
          target="_blank"
          rel="noopener"
          href="https://www.fincen.gov/resources/statutes-and-regulations/usa-patriot-act"
          >Patriot Act</a
        >.
      </p>
      <br />
      <p>
        Information used for the purpose of web analytics is collected pursuant
        to Innovation, Science and Economic Development Canada's enabling
        statutes. Such data may be used for communications and information
        technology statistical purposes, audit, evaluation, research, planning
        and reporting. For more information on how your privacy is safeguarded
        in relation to web analytics, see the
        <a
          target="_blank"
          rel="noopener"
          href="https://www.tbs-sct.gc.ca/pol/doc-eng.aspx?id=26761"
          >Standard on Privacy and Web Analytics</a
        >.
      </p>
      <br />
    </section>
    <section id="protecting_security">
      <h2 class="subtitle is-size-3">
        Protecting the security of Government of Canada websites
      </h2>
      <p>
        BizPaL employs software programs to monitor network traffic to identify
        unauthorized attempts to upload or change information, or otherwise
        cause damage. This software receives and records the IP address of the
        computer that has contacted our website, the date and time of the visit
        and the pages visited. We make no attempt to link these addresses with
        the identity of individuals visiting our site unless an attempt to
        damage the site has been detected.
      </p>
      <br />
      <p>
        This information is collected pursuant to
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/f-11/"
          >section 161 of the Financial Administration Act</a
        >. The information may be shared with appropriate law enforcement
        authorities if suspected criminal activities are detected. Such
        information may be used for network security related statistical
        purposes, audit, evaluation, research, planning and reporting and is
        included in Personal Information Bank PSU 914-Public Communications.
      </p>
      <br />
      <h3 class="subtitle is-size-4">Privacy inquiry</h3>
      <p>
        Any questions, comments, concerns or complaints you may have regarding
        the administration of the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/P-21/index.html"
          >Privacy Act</a
        >
        and privacy policies regarding BizPaL's web presence may be directed to
        the departmental Privacy Coordinator by email, by telephone or in
        writing to:
      </p>
      <p class="address">
        Access to Information and Privacy (ATIP) Services<br />
        Innovation, Science and Economic Development Canada<br />
        2nd Floor, West Tower<br />
        C.D. Howe Building<br />
        235 Queen Street<br />
        Ottawa, Ontario K1A 0H5<br />
        <br />
        Telephone: 343-291-2792<br />
        Fax: 343-291-1893<br />
        Email:
        <a href="mailto:ic.atip-aiprpa.ic@canada.ca"
          >ic.atip-aiprpa.ic@canada.ca</a
        ><br />
      </p>
      <p>
        If you are not satisfied with our response to your privacy concern, you
        may wish to contact the
        <a target="_blank" rel="noopener" href="https://www.priv.gc.ca/en/"
          >Office of the Privacy Commissioner</a
        >
        by email at
        <a href="mailto:info@priv.gc.ca">info@priv.gc.ca</a> or by telephone at
        1‑800‑282‑1376.
      </p>
      <br />
    </section>
    <section id="outside_files">
      <h2 class="subtitle is-size-3">
        Using files located on non-Government of Canada servers
      </h2>
      <p>
        To improve the functionality of Government of Canada websites, certain
        files (such as open source libraries, images and scripts) may be
        delivered automatically to your browser via a trusted third-party server
        or content delivery network. The delivery of these files is intended to
        provide a seamless user experience by speeding response times and
        avoiding the need for each visitor to download these files. Where
        applicable, specific privacy statements covering these files are
        included in our Privacy Notice.
      </p>
      <br />
    </section>
    <section id="official_languages">
      <h2 class="subtitle is-size-3">
        Providing content in our official languages
      </h2>
      <p>
        The
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/O-3.01/"
          >Official Languages Act</a
        >, the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws.justice.gc.ca/eng/regulations/SOR-92-48/index.html"
          >Official Languages (Communications with and Services to the Public)
          Regulations</a
        >
        and Treasury Board policy requirements establish when we use both
        English and French to provide services to or communicate with members of
        the public. When there is no obligation to provide information in both
        official languages, content may be available in one official language
        only. Information provided by organizations not subject to the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/O-3.01/"
          >Official Languages Act</a
        >
        is in the language(s) provided. Information provided in a language other
        than English or French is only for the convenience of our visitors.
      </p>
      <br />
    </section>
    <section id="website_linking">
      <h2 class="subtitle is-size-3">
        Linking to non-Government of Canada websites
      </h2>
      <p>
        Links to websites not under the control of the Government of Canada are
        provided solely for the convenience of our website visitors. We are not
        responsible for the accuracy, currency or reliability of the content of
        such websites. The Government of Canada does not offer any guarantee in
        that regard and is not responsible for the information found through
        these links, nor does it endorse the sites and their content.
      </p>
      <br />
      <p>
        Visitors should also be aware that information offered by non-Government
        of Canada sites to which this website links is not subject to the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/P-21/index.html"
          >Privacy Act</a
        >
        or the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/O-3.01/"
          >Official Languages Act</a
        >
        and may not be accessible to persons with disabilities. The information
        offered may be available only in the language(s) used by the sites in
        question. With respect to privacy, visitors should research the privacy
        policies of these non-government websites before providing personal
        information.
      </p>
      <br />
    </section>
    <section id="ownership_content">
      <h2 class="subtitle is-size-3">
        Ownership and usage of content provided on this site
      </h2>
      <p>
        Materials on this website were produced and/or compiled by BizPaL for
        the purpose of providing Canadians with access to information about the
        licenses and permits offered by the Government of Canada,
        Provincial/Territorial, and municipal governments. You may use and
        reproduce the materials as follows:
      </p>
      <br />
      <h3 class="subtitle is-size-4">Non-commercial reproduction</h3>
      <p>
        Unless otherwise specified, you may reproduce the materials in whole or
        in part for non-commercial purposes, and in any format, without charge
        or further permission, provided you do the following:
      </p>
      <ul class="terms-list">
        <li>
          exercise due diligence in ensuring the accuracy of the materials
          reproduced;
        </li>
        <li>
          indicate both the complete title of the materials reproduced as well
          as the author (where available); and
        </li>
        <li>
          indicate that the reproduction is a copy of the version available at
          <em>[URL where original document is available]</em>.
        </li>
      </ul>
      <h3 class="subtitle is-size-4">Commercial reproduction</h3>
      <p>
        Unless otherwise specified, you may not reproduce materials on this
        site, in whole or in part, for the purposes of commercial redistribution
        without prior written permission from Innovation, Science and Economic
        Development Canada.
      </p>
      <br />
      <p>
        To obtain permission to reproduce Government of Canada materials on this
        site for commercial purposes, apply for
        <a
          target="_blank"
          rel="noopener"
          href="http://www.ic.gc.ca/eic/site/icgc.nsf/eng/07413.html"
          >crown copyright clearance</a
        >
        or write to:
      </p>
      <p class="address">
        ISED Citizen Services Centre<br />
        Innovation, Science and Economic Development Canada<br />
        C.D. Howe Building<br />
        4th Floor, East Tower<br />
        235 Queen Street<br />
        Ottawa, Ontario, Canada<br />
        K1A 0H5<br />
        <br />
        Email:
        <a href="mailto:ISED@canada.ca">ISED@canada.ca</a><br />
      </p>
      <p>
        You may also contact us to obtain additional information concerning
        copyright ownership and restrictions, as some of the content on this
        site may be subject to the copyright of another party. Where information
        has been produced or copyright is not held by Government of Canada, the
        materials are protected under the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/C-42/index.html"
          >Copyright Act</a
        >
        and international agreements. Details concerning copyright ownership are
        indicated on the relevant page(s).
      </p>
      <br />
    </section>
    <section id="trademark_notice">
      <h2 class="subtitle is-size-3">Trademark notice</h2>
      <p>
        The official symbols of the Government of Canada, including the Canada
        wordmark, the arms of Canada, the flag symbol, and those of other
        provinces and territories may not be reproduced, whether for commercial
        or non-commercial purposes, without prior
        <a
          target="_blank"
          rel="noopener"
          href="https://www.canada.ca/en/treasury-board-secretariat/topics/government-communications/federal-identity-requirements/legal-protection-official-symbols-government-canada.html"
          >written authorization</a
        >.
      </p>
      <br />
      <h3 class="subtitle is-size-4">Copyright</h3>
      <p>
        Information that we post is subject to the
        <a
          target="_blank"
          rel="noopener"
          href="https://laws-lois.justice.gc.ca/eng/acts/C-42/index.html"
          >Copyright Act</a
        >.
      </p>
      <br />
    </section>
    <section id="accessibility_commitment">
      <h2 class="subtitle is-size-3">Our commitment to accessibility</h2>
      <p>
        The Government of Canada is committed to achieving a high standard of
        accessibility as defined in the
        <a
          target="_blank"
          rel="noopener"
          href="https://www.tbs-sct.gc.ca/pol/doc-eng.aspx?id=23601"
          >Standard on Web Accessibility</a
        >
        and the
        <a
          target="_blank"
          rel="noopener"
          href="https://www.tbs-sct.gc.ca/pol/doc-eng.aspx?id=27088"
          >Standard on Optimizing Websites and Applications for Mobile
          Devices</a
        >. In the event of difficulty using our Web pages, applications or
        device-based mobile applications, please contact us for assistance or to
        obtain alternative formats such as regular print, Braille or another
        appropriate format.
      </p>
      <br />
    </section>
    <section id="media_requests">
      <h2 class="subtitle is-size-3">Questions and media requests</h2>
      <p>
        Reporters are asked to send questions to:
      </p>
      <p class="address">
        Telephone: 343-291-1777<br />
        Email:
        <a href="mailto:ic.mediarelations-mediasrelations.ic@canada.ca"
          >ic.mediarelations-mediasrelations.ic@canada.ca</a
        ><br />
        Business hours (Eastern time): 7:30 a.m. to 6 p.m.<br />
      </p>
    </section>
    <section>
      <h2 class="subtitle is-size-3">Disclaimer</h2>
      <p>
        Information on this website comes from many different federal
        departments and agencies, as well as external sources and other levels
        of government. Every effort is made to ensure the accuracy, currency and
        reliability of the content. Users who wish to use this information or to
        avail themselves of the services may contact BizPaL in their province or
        territory.
      </p>
    </section>
  </div>
</template>

<script>
export default {
  name: "terms"
};
</script>

<style></style>
